'use client';

import React from 'react';
import { Typography, Tooltip, PulseIconButton } from 'src/components';
import {
  faPencil,
  faTrashCanXmark,
  faArrowRotateLeft,
} from '@fortawesome/pro-solid-svg-icons';
import { CohortStatusLabel } from 'src/components/CohortStatusLabel';
import type { CohortStatus } from 'src/api/redux/providerCohorts';
import type { CodeSetItemKind } from '../types';
import { CODE_SET_KIND_SUBTITLES } from '../constants';

export interface CodeSetItemRowProps {
  label: string;
  kind: CodeSetItemKind;
  status?: string;
  /** Overrides the subtitle derived from `kind`. */
  subtitle?: string;
  isSelected?: boolean;
  disabled?: boolean;
  /** Tooltip shown when `disabled` (e.g. "Cannot filter by encounter items…"). */
  disabledReason?: string;
  onSelect?: () => void;
  onEdit?: (e: React.MouseEvent) => void;
  onDelete?: (e: React.MouseEvent) => void;
  onReactivate?: (e: React.MouseEvent) => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  'data-testid'?: string;
  className?: string;
}

/**
 * The single row used by the code-set / volume selector dropdowns (Discovery,
 * Lists column selector, profile selector). All row formatting — label, subtitle,
 * status chip, selection state, and hover quick-actions — lives here so the
 * dropdowns stay visually consistent and design changes happen in one place.
 */
const CodeSetItemRow: React.FC<CodeSetItemRowProps> = ({
  label,
  kind,
  status,
  subtitle,
  isSelected = false,
  disabled = false,
  disabledReason,
  onSelect,
  onEdit,
  onDelete,
  onReactivate,
  draggable = false,
  onDragStart,
  onDragEnd,
  'data-testid': testId,
  className = '',
}) => {
  const canReactivate = kind === 'cohort' && status === 'DEACTIVATED';
  const hasActions = Boolean(onEdit || onDelete || (canReactivate && onReactivate));

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.();
    }
  };

  const row = (
    <div
      className={`group flex items-center justify-between gap-2 px-4 py-2 ${
        disabled
          ? 'opacity-50 cursor-not-allowed bg-neutral-50'
          : 'cursor-pointer hover:bg-neutral-100'
      } ${className}`}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={handleKeyDown}
      draggable={draggable && !disabled}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-label={`Select ${label}`}
      data-testid={testId}
    >
      <div className="flex flex-col items-start gap-0.5 min-w-0 flex-1">
        <Typography
          className={`text-sm font-semibold break-words ${
            isSelected ? 'text-sky-600' : 'text-text-primary'
          }`}
        >
          {label}
        </Typography>
        <div className="flex items-baseline flex-wrap">
          <Typography className="text-xs text-tertiary">
            {subtitle ?? CODE_SET_KIND_SUBTITLES[kind]}
          </Typography>
          <CohortStatusLabel status={status as CohortStatus | undefined} />
        </div>
      </div>

      {hasActions && (
        <div
          className="flex items-center gap-3 shrink-0"
          role="group"
          aria-label={`Actions for ${label}`}
        >
          {canReactivate && onReactivate && (
            <PulseIconButton
              icon={faArrowRotateLeft}
              variant="plain"
              color="neutral"
              size="sm"
              tooltip="Reactivate"
              className="invisible group-hover:visible text-neutral-600"
              onClick={(e) => {
                e.stopPropagation();
                onReactivate(e);
              }}
              aria-label={`Reactivate ${label}`}
              data-testid={testId ? `${testId}-reactivate` : undefined}
            />
          )}
          {onEdit && (
            <PulseIconButton
              icon={faPencil}
              variant="plain"
              color="neutral"
              size="sm"
              tooltip="Edit"
              className="invisible group-hover:visible text-neutral-600"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(e);
              }}
              aria-label={`Edit ${label}`}
              data-testid={testId ? `${testId}-edit` : undefined}
            />
          )}
          {onDelete && (
            <PulseIconButton
              icon={faTrashCanXmark}
              variant="plain"
              color="neutral"
              size="sm"
              tooltip="Delete"
              className="invisible group-hover:visible text-neutral-600"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(e);
              }}
              aria-label={`Delete ${label}`}
              data-testid={testId ? `${testId}-delete` : undefined}
            />
          )}
        </div>
      )}
    </div>
  );

  if (disabled && disabledReason) {
    return <Tooltip content={disabledReason}>{row}</Tooltip>;
  }

  return row;
};

export default CodeSetItemRow;
